// Renderer-agnostic helpers for the searched-area boundary feature.
//
// The region inference + boundary fetch are shared between the dormant HERE
// implementation (useGetMapBoundary) and the active MapLibre one
// (useMapLibreBoundary). Only the drawing differs per renderer.

import { ServiceConfig } from '@user/store/services/config'
import { get, isUnexpectedResponseError } from '@user/store/services/crud'
import { HereResult } from '@user/store/services/hereGeoCoder'
import { LatLng } from '@user/store/map/typings'

const apiBase = `${ServiceConfig.baseAPIUrl}/mapgeodata`

export type BoundaryQuery = {
  regionType: 'zip' | 'county' | 'city' | 'state'
  regionId: string
  state?: string
  country: string
}

export type BoundaryRingsResponse = {
  rings: LatLng[][]
  featureName?: string | null
  featureId?: string | null
}

/** Infer country code from a HERE result — returns 'us' or 'ca'. */
function inferCountry(r: HereResult): string {
  const countryCode = r.address?.countryCode?.trim().toUpperCase()
  if (countryCode === 'CAN' || countryCode === 'CA') return 'ca'
  return 'us'
}

/** Prefer stateCode if present, else undefined (let the server resolve). */
function pickStateCode(r: HereResult): string | undefined {
  const code = r.address?.stateCode?.trim()
  if (code && code.length <= 3) return code.toUpperCase()
  return undefined
}

/** Infer a mappable region (zip/county/city/state) from a HERE result. */
export function inferBoundaryQuery(r: HereResult): BoundaryQuery | null {
  const a = r.address
  const country = inferCountry(r)

  // ZIP
  if (
    r.resultType === 'locality' &&
    r.localityType === 'postalCode' &&
    a?.postalCode
  ) {
    return {
      regionType: 'zip',
      regionId: a.postalCode.padStart(5, '0'),
      state: pickStateCode(r),
      country,
    }
  }

  // County
  if (
    r.resultType === 'administrativeArea' &&
    r.administrativeAreaType === 'county' &&
    a?.county
  ) {
    const st = pickStateCode(r)
    if (st)
      return {
        regionType: 'county',
        regionId: a.county.trim(),
        state: st,
        country,
      }
  }

  // City
  if (
    r.resultType === 'locality' &&
    r.localityType === 'city' &&
    (a?.city || a?.district)
  ) {
    const city = a?.city ?? a?.district
    if (city) {
      const st = pickStateCode(r)
      if (st)
        return { regionType: 'city', regionId: city.trim(), state: st, country }
    }
  }

  // State
  if (
    r.resultType === 'administrativeArea' &&
    r.administrativeAreaType === 'state'
  ) {
    const st = pickStateCode(r)
    if (st) return { regionType: 'state', regionId: st, country }
  }

  return null
}

/**
 * Fetch the boundary rings for an inferred region. Returns null when the API
 * reports no boundary; throws on an unexpected (network/5xx) response so the
 * caller can decide how to surface it.
 */
export async function fetchBoundaryRings(
  query: BoundaryQuery,
  countryOverride?: string,
): Promise<BoundaryRingsResponse | null> {
  const params = new URLSearchParams({
    regionType: query.regionType,
    regionId: query.regionId,
  })
  if (query.state) params.set('state', query.state)
  const country = countryOverride ?? query.country
  if (country) params.set('country', country)

  const resp = await get(`${apiBase}/boundary?${params.toString()}`)
  if (isUnexpectedResponseError(resp)) {
    throw new Error(`Boundary request failed (${resp.status})`)
  }

  const data: BoundaryRingsResponse = await (resp as Response).json()
  if (!data?.rings?.length) return null
  return data
}

type Ring = number[][] // [[lng, lat], ...]

function signedArea(ring: LatLng[]): number {
  let area = 0
  for (let i = 0; i < ring.length - 1; i++) {
    const j = i + 1
    area += (ring[j].lng - ring[i].lng) * (ring[j].lat + ring[i].lat)
  }
  return area / 2
}

function ringToCoords(ring: LatLng[]): Ring {
  const coords = ring.map((p) => [p.lng, p.lat])
  // GeoJSON linear rings must be closed.
  const first = coords[0]
  const last = coords[coords.length - 1]
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    coords.push([first[0], first[1]])
  }
  return coords
}

/**
 * Convert raw boundary rings into a GeoJSON MultiPolygon. Mirrors the HERE
 * implementation's hole handling: the largest-area ring is an exterior, and
 * smaller / opposite-wound rings nested under it become holes (subtraction).
 */
export function ringsToGeoJsonGeometry(rings: LatLng[][]): {
  type: 'MultiPolygon'
  coordinates: Ring[][]
} {
  const ringsWithArea = rings
    .map((ring) => {
      const sa = signedArea(ring)
      return { ring, signedArea: sa, absArea: Math.abs(sa) }
    })
    .filter((r) => r.ring.length >= 3)
    .sort((a, b) => b.absArea - a.absArea)

  const polygons: Ring[][] = []
  const used = new Set<number>()

  for (let i = 0; i < ringsWithArea.length; i++) {
    if (used.has(i)) continue
    const exterior = ringsWithArea[i]
    const polygon: Ring[] = [ringToCoords(exterior.ring)]

    for (let j = i + 1; j < ringsWithArea.length; j++) {
      if (used.has(j)) continue
      const candidate = ringsWithArea[j]
      const isOppositeWinding =
        exterior.signedArea > 0 !== candidate.signedArea > 0
      const isMuchSmaller = candidate.absArea < exterior.absArea * 0.8
      if (isOppositeWinding || isMuchSmaller) {
        polygon.push(ringToCoords(candidate.ring))
        used.add(j)
      }
    }

    polygons.push(polygon)
    used.add(i)
  }

  return { type: 'MultiPolygon', coordinates: polygons }
}
